import React, { useState } from "react";
import axios from "axios";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function isValidEmail(s) {
  if (!s || typeof s !== "string") return false;
  const trimmed = s.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * Post-announcement "What's next?" waitlist form.
 * Captures emails to /api/newsletter.
 */
export const WaitlistForm = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorMsg(t("waitlist_error_invalid"));
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim() });
      setStatus("success");
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string" && detail
          ? detail
          : t("waitlist_error_generic"),
      );
    }
  };

  return (
    <section className="fade-up" data-testid="waitlist-section">
      <div
        className="brutal-card p-6 md:p-10"
        style={{ background: "#0A0A0A", color: "#FDFBF7" }}
      >
        <p
          className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] mb-3"
          style={{ color: "#FF9933" }}
          data-testid="waitlist-eyebrow"
        >
          {t("waitlist_heading")}
        </p>
        <h2
          className="font-display uppercase leading-none mb-4"
          style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", color: "#FDFBF7" }}
          data-testid="waitlist-heading"
        >
          {t("waitlist_sub")}
        </h2>

        {status === "success" ? (
          <p
            className="text-base md:text-lg font-extrabold uppercase tracking-[0.2em] py-4"
            style={{ color: "#FF9933" }}
            data-testid="waitlist-success"
            role="status"
            aria-live="polite"
          >
            ✓ {t("waitlist_success")}
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 mt-4"
            data-testid="waitlist-form"
            noValidate
          >
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              placeholder={t("waitlist_placeholder")}
              aria-label={t("waitlist_placeholder")}
              disabled={status === "submitting"}
              className="flex-1 border-4 border-black px-4 py-3 text-base font-bold outline-none"
              style={{
                background: "#FDFBF7",
                color: "#0A0A0A",
                minHeight: 56,
              }}
              data-testid="waitlist-email-input"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="brutal-btn px-6 shrink-0"
              style={{ minHeight: 56 }}
              data-testid="waitlist-submit-btn"
            >
              {status === "submitting"
                ? t("waitlist_submitting")
                : t("waitlist_cta")}
            </button>
          </form>
        )}

        {status === "error" && errorMsg && (
          <p
            className="mt-3 text-sm font-bold uppercase tracking-[0.2em]"
            style={{ color: "#FFB4B8" }}
            data-testid="waitlist-error"
            role="alert"
          >
            {errorMsg}
          </p>
        )}
      </div>
    </section>
  );
};

export default WaitlistForm;
